import React from 'react';
import { getPaginationItems, PAGINATION_ELLIPSIS } from '../../lib/pagination';
import { cn } from '../../lib/cn';
import { Icon, type IconKind } from '../Icon/Icon';

export interface PaginationProps {
  page: number;
  total: number;
  onChange: (page: number) => void;
  /** Pages shown on each side of the current page before ellipsis. */
  siblingCount?: number;
  /** `default` — sage active pill; `compact` — message-log style. */
  variant?: 'default' | 'compact';
  className?: string;
}

const defaultBtn =
  'inline-flex h-[34px] min-w-[34px] items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)]';

const compactBtn =
  'inline-flex h-[28px] min-w-[28px] items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[7px] text-[12px] font-semibold text-[var(--ink-2)] transition-[background,color] duration-[var(--dur-fast)] hover:enabled:bg-[var(--surface-mute)] hover:enabled:text-[var(--ink-0)] disabled:cursor-not-allowed disabled:opacity-40';

export function Pagination({
  page,
  total,
  onChange,
  siblingCount = 1,
  variant = 'default',
  className,
}: PaginationProps) {
  if (total <= 1) return null;

  const items = getPaginationItems(page, total, siblingCount);
  const compact = variant === 'compact';
  const btnClass = compact ? compactBtn : defaultBtn;
  const prevIcon: IconKind = compact ? 'left' : 'arrow-l';
  const nextIcon: IconKind = compact ? 'right' : 'arrow-r';
  const gapClass = compact ? 'gap-[5px]' : 'gap-1.5';

  return (
    <div className={cn('flex items-center', gapClass, className)}>
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className={btnClass}
        aria-label="Previous page"
      >
        <Icon kind={prevIcon} size={compact ? 14 : 14} stroke={compact ? 2.2 : 2} />
      </button>

      {items.map((item, index) => {
        if (item === PAGINATION_ELLIPSIS) {
          return (
            <span
              key={`ellipsis-${index}`}
              className={cn(
                btnClass,
                'pointer-events-none min-w-[28px] border-transparent bg-transparent text-[var(--ink-3)]',
              )}
              aria-hidden
            >
              …
            </span>
          );
        }

        const active = item === page;
        return (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              btnClass,
              compact
                ? active && 'border-[var(--ink-0)] bg-[var(--ink-0)] text-[var(--cream-1)]'
                : cn(
                    'w-[34px] font-[var(--font-mono)] text-[12.5px] font-semibold',
                    active ? 'bg-[var(--sage-0)] text-white' : 'text-[var(--ink-2)]',
                  ),
            )}
          >
            {item}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => onChange(Math.min(total, page + 1))}
        disabled={page === total}
        className={btnClass}
        aria-label="Next page"
      >
        <Icon kind={nextIcon} size={compact ? 14 : 14} stroke={compact ? 2.2 : 2} />
      </button>
    </div>
  );
}
